import { gsap } from 'gsap'
import PropTypes from 'prop-types'
import { memo, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import constants from 'app/constants'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import ButtonActivity from '../ButtonActivity/ButtonActivity'
import styles from './ButtonAccountStatus.module.scss'

/**
 * The `ButtonAccountStatus`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ButtonAccountStatus (props) {
  const { className, ...rest } = props
  const { showModal } = useModalContext()
  const navigate = useNavigate()
  const { userActivityStatus } = useUserContext()
  const { account } = useUserContext()
  const classNameOutput = useMemo(
    () => classNames([styles.button, className]),
    [className]
  )

  /**
   * Memo: The label
   */
  const label = useMemo(() => {
    if (constants.MODE === 0) {
      // Landing page
      return 'Apply Now'
    }

    if (!account.hasWallet || !account.isSignedIn) {
      return 'Sign in'
    }

    return 'My Account'
  }, [account])

  /**
   * Memo: The button props
   */
  const buttonProps = useMemo(() => {
    if (constants.MODE === 0 || !account.hasWallet || !account.isSignedIn) {
      return {}
    }

    return {
      Tag: Link,
      to: '/account',
      onClick: noop
    }
  }, [account])

  /**
   * Scroll to the `Apply` section
   */
  const onScrollToApply = useCallback(() => {
    const targetElement = document.getElementById('apply-form')

    if (!targetElement) {
      // Defensive
      return
    }

    const scrollY = window.scrollY

    if (targetElement) {
      const elementPosition = targetElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + scrollY

      gsap.to(window, {
        duration: Math.min(2, (offsetPosition - scrollY) * 0.0002),
        scrollTo: { y: offsetPosition },
        ease: 'power2.out' // You can adjust the ease function as desired
      })
    }
  }, [])

  /**
   * Handle click
   */
  const handleClick = useCallback(async () => {
    if (constants.MODE === 0) {
      onScrollToApply()

      return
    }

    if (!account.hasWallet || !account.isSignedIn) {
      const res = await showModal({ id: 'app:sign_in' })

      if (res === 'view_account') {
        navigate('/account')
      }

      return
    }

    // Can navigate (NOTE: When account is available, `buttonProps` overrides this behavior)
    navigate('/account')
  }, [account])

  return (
    <ButtonActivity
      isBusy={!!userActivityStatus && constants.MODE !== 0}
      className={classNameOutput}
      {...rest}
      onClick={handleClick}
      {...buttonProps}
    >
      {label}
    </ButtonActivity>
  )
}

/** @type {object} */
ButtonAccountStatus.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
ButtonAccountStatus.defaultProps = {
  className: ''
}

// Memoize
export default memo(ButtonAccountStatus)
